import {
  AppointmentChoiceEligibilityQueryKey,
  Appointments as AppointmentsRoute,
  AppointmentChoiceEligibility as AppointmentChoiceEligibilityRoute,
} from 'api/appointments/routes'
import useAxiosWithAuth from 'api/useAxiosWithAuth'
import { Provider } from 'hooks/useUser/useUser'
import { useMemo } from 'react'
import { useQuery } from 'react-query'

export type AppointmentType = {
  id: string
  reason: string
  category: string
  followupModule: string
}

export type Appointment = {
  id: string
  sourceId: string
  user: string
  provider: Provider
  time: string
  isCancelled?: boolean
  videoCallLink?: string
  type: AppointmentType
}

export type AppointmentDefinition = {
  id: string
  title: string
  description: string
  icon: string // URL
  buttonText?: string
}

interface UseAppointmentsInterface {
  isLoading: boolean
  appointments: Appointment[]
  nonCancelledAppointments: Appointment[]
  hasBookedAtleastTwoMd: boolean
  hasBookedRd: boolean
  isEligibleToChooseAppointmentType: boolean
  isAppointmentChoiceEligibilityLoading: boolean
  hasBookedNumberRDAppointments: (numAppointments: number) => boolean
  hasBookedNumberMDAppointments: (numAppointments: number) => boolean
  refetch: () => void
}

const useAppointments = (): UseAppointmentsInterface => {
  const axiosWithAuth = useAxiosWithAuth()

  const fetchAppointments = async (): Promise<Appointment[]> => {
    try {
      const res = await axiosWithAuth.get<Appointment[]>(AppointmentsRoute)
      return res.data
    } catch (err) {
      console.error('Failed to fetch appointments:', err)
      return []
    }
  }

  const { isLoading, data, refetch } = useQuery(
    AppointmentsRoute,
    fetchAppointments
  )

  const appointments = useMemo(() => data ?? [], [data])

  const fetchAppointmentChoiceEligibility = async (): Promise<boolean> => {
    return await axiosWithAuth
      .get<boolean>(AppointmentChoiceEligibilityRoute)
      .then((res) => res.data)
      .catch(() => false)
  }

  const {
    data: isEligibleToChooseAppointmentTypeData,
    isLoading: isAppointmentChoiceEligibilityLoading,
  } = useQuery(
    AppointmentChoiceEligibilityQueryKey,
    fetchAppointmentChoiceEligibility
  )

  const isEligibleToChooseAppointmentType =
    isEligibleToChooseAppointmentTypeData ?? false

  const nonCancelledAppointments = useMemo(
    () => appointments.filter((appointment) => !appointment.isCancelled),
    [appointments]
  )

  const hasBookedAtleastTwoMd = useMemo(
    () =>
      appointments.filter(
        (a) => a.provider.type === 'Medical Provider' && !a.isCancelled
      ).length > 1,
    [appointments]
  )

  const hasBookedNumberMDAppointments = (numAppointments: number) =>
    appointments.filter(
      (a) => a.provider.type === 'Medical Provider' && !a.isCancelled
    ).length > numAppointments

  const hasBookedNumberRDAppointments = (numAppointments: number) =>
    appointments.filter(
      (a) => a.provider.type === 'Dietician' && !a.isCancelled
    ).length > numAppointments

  const hasBookedRd = useMemo(
    () =>
      appointments.filter(
        (a) => a.provider.type === 'Dietician' && !a.isCancelled
      ).length > 0,
    [appointments]
  )

  return {
    isLoading,
    appointments,
    nonCancelledAppointments,
    refetch,
    hasBookedRd,
    hasBookedAtleastTwoMd,
    isEligibleToChooseAppointmentType,
    isAppointmentChoiceEligibilityLoading,
    hasBookedNumberRDAppointments,
    hasBookedNumberMDAppointments,
  }
}

export default useAppointments
